import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../../../core/services/user.service';
import { ModalService } from '../../../../core/services/modal.service';
import { AddSubAccountModalComponent } from '../../components/add-sub-account-modal';
import { UpgradeAccountModalComponent } from '../../components/upgrade-account-modal';
import { Subscription } from 'rxjs';
import { LucideAngularModule, Shield } from 'lucide-angular';

interface ManagedAccount {
  _id: string;
  firstName: string;
  lastName: string;
  sex: 'male' | 'female';
  birthday: string;
  phoneNumbers: Array<{
    countryCode: string;
    number: string;
    type: string;
    isPrimary: boolean;
  }>;
  accountType: string;
  email?: string;
  passportNumber?: string;
}

@Component({
  selector: 'app-managed-accounts',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './managed-accounts.component.html',
  styleUrls: ['./managed-accounts.component.scss'],
})
export class ManagedAccountsComponent implements OnInit, OnDestroy {
  managedAccounts: ManagedAccount[] = [];
  filteredAccounts: ManagedAccount[] = [];
  searchTerm: string = '';
  selectedStatus: string = 'all';
  loading: boolean = false;
  error: string | null = null;
  showAddModal: boolean = false;
  private modalSubscription?: Subscription;
  shieldIcon = Shield;

  constructor(
    private userService: UserService,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    this.loadManagedAccounts();
    this.setupModalListener();
  }

  ngOnDestroy() {
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
  }

  setupModalListener() {
    this.modalSubscription = this.modalService
      .getEvents()
      .subscribe((event) => {
        if (event.type === 'subAccountCreated') {
          // Recharger la liste des comptes gérés
          this.loadManagedAccounts();
        } else if (event.type === 'accountUpgraded') {
          // Recharger la liste des comptes gérés après mise à niveau
          this.loadManagedAccounts();
        }
      });
  }

  loadManagedAccounts() {
    this.loading = true;
    this.error = null;

    this.userService.getSubAccounts().subscribe({
      next: (response: any) => {
        this.managedAccounts =
          response.subAccounts || response.data || response || [];
        this.filteredAccounts = [...this.managedAccounts];
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Erreur lors du chargement des comptes gérés:', err);
        this.error = 'Erreur lors du chargement des comptes gérés';
        this.loading = false;
        // Fallback vers les données mockées en cas d'erreur
        this.loadMockData();
      },
    });
  }

  loadMockData() {
    this.managedAccounts = [
    ];
    this.filteredAccounts = [...this.managedAccounts];
  }

  onSearchChange() {
    this.filterAccounts();
  }

  onStatusChange() {
    this.filterAccounts();
  }

  filterAccounts() {
    this.filteredAccounts = this.managedAccounts.filter((account) => {
      const matchesSearch =
        account.firstName
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase()) ||
        account.lastName
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase()) ||
        (account.email &&
          account.email
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase())) ||
        (account.passportNumber &&
          account.passportNumber
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase()));

      const matchesStatus =
        this.selectedStatus === 'all' ||
        account.accountType === this.selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }

  getSexText(sex: string): string {
    return sex === 'male' ? 'Homme' : 'Femme';
  }

  getSexClass(sex: string): string {
    return sex === 'male'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-pink-100 text-pink-800';
  }

  getAccountTypeText(accountType: string): string {
    switch (accountType) {
      case 'SUB_ACCOUNT':
        return 'Sous-compte';
      case 'MAIN_ACCOUNT':
        return 'Compte principal';
      case 'UNDERCOVER':
        return 'Sous-couvert';
      default:
        return accountType;
    }
  }

  getAccountTypeClass(accountType: string): string {
    switch (accountType) {
      case 'SUB_ACCOUNT':
        return 'bg-green-100 text-green-800';
      case 'MAIN_ACCOUNT':
        return 'bg-blue-100 text-blue-800';
      case 'UNDERCOVER':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getPrimaryPhone(phoneNumbers: any[]): string {
    const primaryPhone = phoneNumbers?.find((phone) => phone.isPrimary);
    if (primaryPhone) {
      return `${primaryPhone.countryCode} ${primaryPhone.number}`;
    }
    return phoneNumbers?.length > 0
      ? `${phoneNumbers[0].countryCode} ${phoneNumbers[0].number}`
      : 'Non renseigné';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Non renseigné';

    // Si la date est déjà au format DD/MM/YYYY
    if (dateString.includes('/')) {
      return dateString;
    }

    // Sinon, on essaie de la parser
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  // Getters pour les statistiques
  get totalAccounts(): number {
    return this.managedAccounts.length;
  }

  get subAccounts(): number {
    return this.managedAccounts.filter(
      (account) => account.accountType === 'SUB_ACCOUNT'
    ).length;
  }

  get mainAccounts(): number {
    return this.managedAccounts.filter(
      (account) => account.accountType === 'MAIN_ACCOUNT'
    ).length;
  }

  get undercoverAccounts(): number {
    return this.managedAccounts.filter(
      (account) => account.accountType === 'UNDERCOVER'
    ).length;
  }

  get otherAccounts(): number {
    return this.managedAccounts.filter(
      (account) =>
        account.accountType !== 'SUB_ACCOUNT' &&
        account.accountType !== 'MAIN_ACCOUNT' &&
        account.accountType !== 'UNDERCOVER'
    ).length;
  }

  addNewAccount() {
    this.modalService.open({
      component: AddSubAccountModalComponent,
      title: 'Ajouter un sous-compte',
      width: '600px',
      closable: true,
    });
  }

  upgradeToMainAccount(account: ManagedAccount) {
    this.modalService.open({
      component: UpgradeAccountModalComponent,
      data: { account },
      title: `Mise à niveau compte de ${account.firstName} ${account.lastName}`,
      width: '500px',
      closable: true,
    });
  }

  editAccount(account: ManagedAccount) {
    console.log('Edit account:', account);
    // TODO: Implement edit account logic
  }

  removeAccount(account: ManagedAccount) {
    if (
      confirm(
        `Êtes-vous sûr de vouloir supprimer l'accès pour ${account.firstName} ${account.lastName} ?`
      )
    ) {
      console.log('Remove account:', account);
      // TODO: Implement remove account logic using userService.detachSubAccount()
    }
  }

  refreshAccounts() {
    this.loadManagedAccounts();
  }
}
